import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  LayoutDashboard,
  FileText,
  Users,
  MapPin,
  User,
  FileLineChart,
  CheckCheck,
  PieChart,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarGroup,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar"; 
import png from "../../assets/TamilNadu_Logo.png";
import PackageJson from "../../../package.json";

const sidebarItems = {
  applicant: [
    { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
    { title: "Scheme", url: "/scheme", icon: FileText },
    { title: "Profile", url: "/profile", icon: User },
    {
      title: "Reference Documents",
      url: "/reference_documents",
      icon: FileText,
    },
    {
      title: "Terms And Condition",
      url: "/terms_and_condition",
      icon: CheckCheck,
    },
    { title: "How To Apply", url: "/how_to_apply", icon: PieChart },
  ],

  district_admin: [
    { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
    { title: "Submissions", url: "/submissions", icon: FileText },
    { title: "Reports", url: "/", icon: FileLineChart },
    { title: "Profile", url: "/profile", icon: User },
  ],

  district_visitor: [
    { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
    { title: "Assignments", url: "/assignments", icon: FileText },
    { title: "Profile", url: "/profile", icon: User },
  ],

  admin: [
    { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
    { title: "Submissions", url: "/submissions", icon: FileText },
    {
      title: "User Management",
      url: "/admin/users",
      icon: Users,
      children: [
        { title: "District Users", url: "/admin/district_users" },
        { title: "Applicant Users", url: "/admin/applicant_users" },
      ],
    },
    { title: "District Management", url: "/admin/districts", icon: MapPin },
    { title: "Reports", url: "/admin/report", icon: FileLineChart },
    { title: "Profile", url: "/profile", icon: User },
  ],
};

const AppSidebar = () => {
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();
  const { isMobile, setOpen } = useSidebar();
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [isMobileView, setIsMobileView] = useState(false);

  const items = sidebarItems[user?.role] || [];

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 640);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    items.forEach((item) => {
      if (item.children?.some((child) => child?.url === location.pathname)) {
        setOpenDropdowns((prev) => ({ ...prev, [item.title]: true }));
      }
    });
  }, [location.pathname, items]);

  const toggleDropdown = (title) => {
    setOpenDropdowns((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  return (
    <Sidebar className="w-[256px] h-screen bg-white/70 backdrop-blur-lg backdrop-saturate-150">
      <SidebarHeader className="p-4 text-center">
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="flex flex-col items-center gap-2">
              {!isMobileView && (
                <img
                  src={png}
                  alt="Tamil Nadu Logo"
                  className="w-[60px] object-contain"
                />
              )}
              <h6 className="font-semibold text-md">
                Department of Handlooms கைத்தறி துறை
              </h6>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className="flex-1">
        <SidebarGroup>
          <SidebarMenu>
            {items.map((item) => {
              const isSubActive = item.children?.some(
                (child) => child.url === location.pathname
              );
              const isParentActive = location.pathname === item.url;
              const isSubmenuOpen = openDropdowns[item.title] || isSubActive;

              return (
                <div key={item.title}>
                  {item.children ? (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        className={`py-4 h-[40px] flex items-center gap-2 transition-all rounded-md ${
                          isParentActive || isSubActive
                            ? "bg-primary text-white font-semibold"
                            : "hover:bg-primary hover:text-white"
                        }`}
                        onClick={() => toggleDropdown(item.title)}
                      >
                        {item.icon && <item.icon className="h-4 w-4" />}
                        <span>{item.title}</span>
                        {isSubmenuOpen ? (
                          <ChevronDown size={16} className="ml-auto" />
                        ) : (
                          <ChevronRight size={16} className="ml-auto" />
                        )}
                      </SidebarMenuButton>

                      {isSubmenuOpen && (
                        <SidebarMenuSub>
                          {item.children.map((subItem) => {
                            const isSubItemActive =
                              location.pathname === subItem.url;
                            return (
                              <SidebarMenuSubItem key={subItem.title}>
                                <Link
                                  to={subItem.url}
                                  onClick={() => isMobile && setOpen(false)}
                                  className={`block px-6 py-2 text-sm rounded-md transition-colors ${
                                    isSubItemActive
                                      ? "bg-primary text-white font-semibold"
                                      : "hover:bg-primary hover:text-white"
                                  }`}
                                >
                                  {subItem.title}
                                </Link>
                              </SidebarMenuSubItem>
                            );
                          })}
                        </SidebarMenuSub>
                      )}
                    </SidebarMenuItem>
                  ) : (
                    <SidebarMenuItem>
                      <Link
                        to={item.url}
                        onClick={() => isMobile && setOpen(false)}
                      >
                        <SidebarMenuButton
                          className={`py-4 h-[40px] flex items-center gap-2 transition-all rounded-md ${
                            location.pathname === item.url
                              ? "bg-primary text-white font-semibold"
                              : "hover:bg-primary hover:text-white"
                          }`}
                        >
                          {item.icon && <item.icon className="h-4 w-4" />}
                          <span>{item.title}</span>
                        </SidebarMenuButton>
                      </Link>
                    </SidebarMenuItem>
                  )}
                </div>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="text-sm px-2 pb-2 text-black">
              App Version: {PackageJson?.version}
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AppSidebar;
